// utility class for managing services
// it will also use the window messaging to communicate with the service.
// the service will send a message with the service name and the service data.
// the service will receive a message with the service name and the service data.
// the service will manage the service state.
// it will run as a singleton.
// it will run as a web worker.
// it will stop/start the services.
// it will manage the services.
// it will log the services.
// it will report the services life cycle.

// This is service manager to host and manage services in the application. Services should be registered in the service manager.
// The service manager will start the service and will stop the service when the application is closed.
// The service manager will also manage the service state and will report the service state to the application.
// The service manager will also log the service state and will report the service state to the application.
// The service manager services will be hosted in the web worker.
// The service manager will also use the window messaging to communicate with the service.
// The service will send a message with the service name and the service data.
// The service will receive a message with the service name and the service data.
// The service will introduce promises wrapper for web worker messaging.

import { WebWorker } from "../workers";
import { logger } from "./helpers";
import { IPromises, IService, IServiceManager, IServiceMessage, ServiceStates, ServiceWorkers } from "./interfaces";
import Service from "./Service";

const namespace = "Service Manager";

class ServiceManager implements IServiceManager {
  private _services: { [key in ServiceWorkers]: IService } | undefined;
  private _promises: IPromises = {};
  private _promiseId = 0;

  /**
   *
   */
  constructor() {
    this._services = {} as { [key in ServiceWorkers]: IService };
  }

  public get services(): { [key in ServiceWorkers]: IService } | undefined {
    return this._services;
  }

  public get promises(): IPromises {
    return this._promises;
  }

  public get promiseId(): number {
    return ++this._promiseId;
  }

  public getPromise(id: number) {
    return this._promises[id];
  }

  public getReject(id: number) {
    return this._promises[id].reject;
  }

  public getResolve(id: number) {
    return this._promises[id].resolve;
  }

  public deletePromise(id: number) {
    delete this._promises[id];
  }

  public createServicePromise(message: IServiceMessage): Promise<any> {
    const id = this.promiseId;
    return new Promise((resolve, reject) => {
      this._promises[id] = { resolve, reject };
      this.sendMessageToWorker({ ...message, promiseId: id });
    });
  }

  public startService(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.start();
    }
  }

  public stopService(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.stop();
    }
  }

  public stopAllServices(): void {
    if (this._services) {
      Object.keys(this._services).forEach((serviceName) => {
        this.stopService(serviceName as ServiceWorkers);
      });
    }
  }

  public logService(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.log();
    }
  }

  public logAllServices(): void {
    if (this._services) {
      Object.keys(this._services).forEach((serviceName) => {
        const service = this._services?.[serviceName as ServiceWorkers];
        if (service) {
          service.log();
        }
      });
    }
  }

  public reportService(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.report();
    }
  }

  public reportAllServices(): void {
    if (this._services) {
      Object.keys(this._services).forEach((serviceName) => {
        const service = this._services?.[serviceName as ServiceWorkers];
        if (service) {
          service.report();
        }
      });
    }
  }

  public addService(service: IService): void {
    this._services![service.name] = service;
  }

  public removeService(serviceName: ServiceWorkers): void {
    delete this._services![serviceName];
  }

  public removeAllServices(): void {
    this._services = undefined;
  }

  public getService(serviceName: ServiceWorkers): IService | undefined {
    return this._services?.[serviceName];
  }

  public setServiceState(serviceName: ServiceWorkers, state: ServiceStates): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.state = state;
    }
  }

  public setStartedAt(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.startedAt = new Date();
    }
  }

  public setStoppedAt(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.stoppedAt = new Date();
    }
  }

  public setLastRun(serviceName: ServiceWorkers): void {
    const service = this._services?.[serviceName];
    if (service) {
      service.lastRun = new Date();
    }
  }

  public getServices(): { [key in ServiceWorkers]: IService } | undefined {
    return this._services;
  }

  public getServiceNames(): string[] {
    if (this._services) {
      return Object.keys(this._services);
    }
    return [];
  }

  public getServiceState(serviceName: ServiceWorkers): ServiceStates | undefined {
    const service = this._services?.[serviceName];
    if (service) {
      return service.state;
    }
  }

  public sendMessageToWorker(message: IServiceMessage): void {
    const service = this._services?.[message.serviceName];
    if (service) {
      service.sendMessageToWorker(message);
    }
  }

  public sendMessageToAll(message: any): void {
    for (const serviceName in this._services) {
      const service = this._services[serviceName as ServiceWorkers];
      service.sendMessageToWorker(message);
    }
  }

  public createService(serviceName: ServiceWorkers, worker: Worker): void {
    const webWorker = new WebWorker(worker, serviceName);
    this._services![serviceName] = new Service({
      name: serviceName,
      start: () => { // mark service as starting and send start message to worker, worker will send back service state
        this.sendMessageToWorker({ serviceName, serviceData: { command: "START" } });
        if (this._services?.[serviceName]) {
          this._services[serviceName].state = "STARTING";
          this._services[serviceName].stoppedAt = null;
        }
      },
      stop: () => { // mark service as stopping and send stop message to worker, worker will send back service state
        this.sendMessageToWorker({ serviceName, serviceData: { command: "STOP" } });
        if (this._services?.[serviceName]) {
          this._services[serviceName].state = "STOPPING";
        }
      },
      sendMessageToWorker: (message: IServiceMessage) => { // send message to worker
        const serviceName = message.serviceName;
        // inject origin
        message.serviceData.origin = "MAIN_THREAD";
        if (this._services?.[serviceName]) {
          this._services[serviceName].lastCommand = message.serviceData.command;
          this._services[serviceName].lastCommandTime = new Date();
          // throttleStorage.addOrExecute({ // throttle storage will add message to queue and execute it, it will throttle messages to worker
          // name: `${message.serviceName}-${message.serviceData.command}`, delay: 75, callback: () => {
          this._services?.[serviceName].worker.worker.postMessage(message);
          // },
          // });
        }
      },
      log: () => { // log service state
        this.sendMessageToWorker({
          serviceName: serviceName,
          serviceData: { command: "LOG" },
        });
        logger(namespace, "info", `LOG for ${this._services?.[serviceName].name}`, {
          serviceName,
          createdAt: this._services?.[serviceName].createdAt,
          startedAt: this._services?.[serviceName].startedAt,
          stoppedAt: this._services?.[serviceName].stoppedAt,
          lastRun: this._services?.[serviceName].lastRun,
          state: this._services?.[serviceName].state,
          lastCommand: this._services?.[serviceName].lastCommand,
          lastCommandTime: this._services?.[serviceName].lastCommandTime,
          lastCommandResult: this._services?.[serviceName].lastCommandResult,
        });
      },
      report: () => { // report service state
        this.sendMessageToWorker({
          serviceName: serviceName,
          serviceData: { command: "REPORT" },
        });
      },
      lastRun: null,
      worker: webWorker,
      state: "INITIALIZING", // service state can be INITIALIZING, STARTING, RUNNING, STOPPING, STOPPED
      createdAt: new Date(),
      startedAt: null,
      stoppedAt: null,
      error: null,
      lastCommand: null,
      lastCommandTime: null,
      lastCommandResult: null,
    });
  }
}

export default ServiceManager;
